import {AggregationPage} from "crqlar-react-common/dist/models/aggregation/aggregation.model";
import {Folio} from "crqlar-react-common/dist/models/billing/folio.model";
import {Page} from "crqlar-react-common/dist/models/common/page.model";
import api from "crqlar-react-common/dist/services/api";
import {
    DigitalSigningCreateOptions,
    DigitalSigningCreateResponse,
    DigitalSigningDetailsView,
    DigitalSigningError,
    DigitalSigningListRequest,
    DigitalSigningListRow,
    DigitalSigningPrefill,
    DigitalSigningStatusView,
    DigitalSigningTemplateOption
} from "./digitalSigning.model";
import {normalizeSnapshot} from "./digitalSigningSnapshot.util";

/*
    Issued tokens by signing id, for this page session only: the token is only in the create
    response, and the QR code needs it again when the folio dialog is reopened. Never stored and
    never logged - after a reload the folio view offers a new QR code instead.
*/
const issuedTokens = new Map<string, string>();

export const isDigitalSigningError = (error: unknown): error is DigitalSigningError => (
    !!error && typeof error === 'object' && typeof (error as DigitalSigningError).reason === 'string'
);

const toError = (error: any): DigitalSigningError => {
    const message: unknown = error?.response?.data?.message;

    if (error?.response?.status === 409) return {reason: 'CHANGED'};
    return {reason: 'FAILED', message: typeof message === 'string' && message.trim() ? message : undefined};
};

const attempt = async <T>(call: () => Promise<{data: T}>): Promise<T> => {
    try {
        return (await call()).data;
    } catch (error) {
        throw toError(error);
    }
};

export class DigitalSigningService {

    private static URI = "/v3/digitalSigning";
    private static AGGREGATION_URI = "/v3/aggregation/digitalSigning";

    /** Omitted choices use backend defaults; roomNumber: '' explicitly hides the room. */
    static async create(
        folioId: string,
        {roomNumber, language, templateId, profileId, tipStaffResourceId, reservationId}: DigitalSigningCreateOptions = {}
    ): Promise<DigitalSigningCreateResponse> {
        const response = await attempt(() => api.post<DigitalSigningCreateResponse>(
            `${DigitalSigningService.URI}/create`,
            {folioId, roomNumber, language, templateId, profileId, tipStaffResourceId, reservationId}
        ));
        issuedTokens.set(response.id, response.token);
        return response;
    }

    /** Use the same reservation context as create, including for a package's shared folio. */
    static async prefill(folioId: string, reservationId?: string): Promise<DigitalSigningPrefill> {
        const response = await attempt(() => api.post<{
            roomNumber?: string | null
            language?: string | null
            languages?: string[] | null
            templates?: DigitalSigningTemplateOption[] | null
            templateId?: string | null
            persons?: {profileId?: string | null, name?: string | null, roomNumber?: string | null}[] | null
            profileId?: string | null
            staff?: {resourceId?: string | null, name?: string | null, tipServiceId?: string | null}[] | null
            tipStaffResourceId?: string | null
            defaultTipServiceId?: string | null
        }>(`${DigitalSigningService.URI}/prefill`, {folioId, reservationId}));
        return {
            roomNumber: response.roomNumber?.trim() || undefined,
            language: response.language?.trim() || undefined,
            languages: (response.languages ?? []).filter(Boolean),
            templates: (response.templates ?? []).filter(template => !!template?.id),
            templateId: response.templateId?.trim() || undefined,
            persons: (response.persons ?? []).flatMap(person => (person?.profileId ? [{
                profileId: person.profileId,
                name: person.name?.trim() || undefined,
                roomNumber: person.roomNumber?.trim() || undefined,
            }] : [])),
            profileId: response.profileId?.trim() || undefined,
            // Who receives the tip: without either, Create link is blocked
            staff: (response.staff ?? []).flatMap(member => (member?.resourceId ? [{
                resourceId: member.resourceId,
                name: member.name?.trim() || '',
                tipServiceId: member.tipServiceId?.trim() || undefined,
            }] : [])),
            tipStaffResourceId: response.tipStaffResourceId?.trim() || undefined,
            defaultTipServiceId: response.defaultTipServiceId?.trim() || undefined,
        };
    }

    static async details(id: string): Promise<DigitalSigningDetailsView> {
        const response = await attempt(
            () => api.post<DigitalSigningDetailsView>(`${DigitalSigningService.URI}/details`, {id})
        );
        return {...response, snapshot: normalizeSnapshot(response.snapshot)};
    }

    static get(id: string): Promise<DigitalSigningStatusView> {
        return attempt(() => api.post<DigitalSigningStatusView>(`${DigitalSigningService.URI}/get`, {id}));
    }

    static list(request: DigitalSigningListRequest): Promise<Page<DigitalSigningStatusView>> {
        return attempt(() => api.post<Page<DigitalSigningStatusView>>(`${DigitalSigningService.URI}/list`, request));
    }

    static listRows(request: DigitalSigningListRequest): Promise<AggregationPage<DigitalSigningListRow>> {
        return attempt(() => api.post<AggregationPage<DigitalSigningListRow>>(`${DigitalSigningService.AGGREGATION_URI}/list`, request));
    }

    static async latestForFolio(folioId: string): Promise<DigitalSigningStatusView | undefined> {
        return (await DigitalSigningService.list({folioIds: [folioId], limit: {limit: 1, offset: 0}})).content?.[0];
    }

    /**
     * Voids the link. With `keepSigned` only while the guest hasn't used it: a signed (or confirmed)
     * one comes back as it is - Finalize knows the status only from the last poll.
     */
    static async void(id: string, options?: {keepSigned?: boolean}): Promise<DigitalSigningStatusView> {
        const view = await attempt(() => api.post<DigitalSigningStatusView>(
            `${DigitalSigningService.URI}/void`,
            {id, keepSigned: !!options?.keepSigned}
        ));
        issuedTokens.delete(id);
        return view;
    }

    static confirm(id: string): Promise<Folio> {
        return attempt(() => api.post<Folio>(`${DigitalSigningService.URI}/confirm`, {id}));
    }

    static issuedToken(id?: string): string | undefined {
        return id ? issuedTokens.get(id) : undefined;
    }
}
